import { useState } from 'react';
import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { claimStatusLabel, contractStatusLabel } from '@shared/lib/statusLabels';
import type { Claim, Contract } from '@entities/legal/types';
import { ContractPicker } from '../ContractPicker';
import {
  AnalyzingBar,
  AnalyzingHint,
  AnalyzingTitle,
  ConfirmActions,
  ConfirmRow,
  ConfirmText,
  ContractHeader,
  fmtMoney,
  getStr,
  Grid,
  LinkedContractCard,
  LinkedContractRow,
  MissingLinkNote,
  ReanalyzeNotice,
  Section,
} from './styles';

const ContextFeedback = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
  padding-left: 2px;
`;

const SelectProject = styled(Select)`
  max-width: 340px;
`;

const ContractActions = styled.div`
  display: flex;
  gap: 8px;
`;

interface ClaimHeaderSectionProps {
  claimId: string;
  claim: Claim;
  contract: Contract | null;
  relatedContractContext?: {
    total: number;
    accepted: number;
    rejected: number;
    disputed: number;
    pending: number;
  } | null;
  can_write: boolean;
  isProcessing: boolean;
  statutProjects: Array<{ id: string; code?: string | null; name: string }>;
  isSettingProject: boolean;
  onSetClaimProject: (projectId: string | null) => void;
  onConfirmLink: (contractId: string) => Promise<void>;
  onConfirmUnlink: () => Promise<void>;
  isLinking: boolean;
  isUnlinking: boolean;
  linkNotice: string | null;
}

export function ClaimHeaderSection({
  claim,
  contract,
  relatedContractContext,
  can_write,
  isProcessing,
  statutProjects,
  isSettingProject,
  onSetClaimProject,
  onConfirmLink,
  onConfirmUnlink,
  isLinking,
  isUnlinking,
  linkNotice,
}: ClaimHeaderSectionProps) {
  const [editingContract, setEditingContract] = useState(false);
  const [pendingLink, setPendingLink] = useState<string | null>(null);
  const [pendingLinkLabel, setPendingLinkLabel] = useState<string | null>(null);
  const [pendingUnlink, setPendingUnlink] = useState(false);

  const claimProjectId = (claim as { project_id?: string | null }).project_id ?? '';
  const meta = (claim.analysis_meta ?? {}) as Record<string, unknown>;
  const direction = claim.direction === 'outgoing' ? 'outgoing' : 'incoming';
  const isIncoming = direction === 'incoming';

  const claimedAmount = fmtMoney(meta['claimed_amount']);
  const forecast = getStr(meta, 'forecast') ?? null;

  async function handleLink() {
    if (!pendingLink) return;
    await onConfirmLink(pendingLink);
    setEditingContract(false);
    setPendingLink(null);
  }

  async function handleUnlink() {
    await onConfirmUnlink();
    setPendingUnlink(false);
  }

  return (
    <>
      <Grid>
        <dt>Тип</dt>
        <dd>
          <Badge>{claim.claim_type === 'claim' ? 'претензия' : 'иск'}</Badge>{' '}
          <Badge>{isIncoming ? 'входящая' : 'исходящая'}</Badge>
        </dd>
        <dt>Контрагент</dt>
        <dd>{claim.counterparty}</dd>
        <dt>Предмет спора</dt>
        <dd>{claim.dispute_type ?? '—'}</dd>
        <dt>Сумма требований</dt>
        <dd>
          {claim.amount != null ? claim.amount.toLocaleString('ru-RU') : (claimedAmount ?? '—')}
        </dd>
        <dt>Текущая стадия</dt>
        <dd>
          <Badge>{claimStatusLabel(claim.stage)}</Badge>
        </dd>
        <dt>Прогноз</dt>
        <dd>
          {claim.forecast ??
            (forecast
              ? forecast === 'high'
                ? isIncoming
                  ? 'Высокая вероятность проигрыша'
                  : 'Высокая вероятность победы'
                : forecast === 'medium'
                  ? 'Средняя'
                  : 'Низкая'
              : '—')}
        </dd>
        <dt>Строительный объект (ПТО)</dt>
        <dd>
          <SelectProject
            value={claimProjectId}
            disabled={!can_write || isSettingProject}
            onChange={(e) => onSetClaimProject(e.target.value || null)}
            aria-label="Строительный объект претензии"
          >
            <option value="">— не привязан —</option>
            {statutProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code ? `${p.code} · ` : ''}
                {p.name}
              </option>
            ))}
          </SelectProject>
        </dd>
      </Grid>

      {isProcessing && (
        <Section>
          <h4>Идёт анализ</h4>
          <AnalyzingBar>
            <AnalyzingTitle>
              LLM анализирует {isIncoming ? 'входящую претензию' : 'наш проект'}…
            </AnalyzingTitle>
            <AnalyzingHint>
              {isIncoming
                ? 'Проверяю требования контрагента против договора, пересчитываю неустойку, ищу основания защиты. Готовлю проект ответа от лица АО «САТКОМ». Обычно 1–3 минуты — карточка обновится автоматически.'
                : 'Самопроверка нашего проекта: позиция, расчёт, СИД, доказательства, что ответит контрагент. Обычно 1–3 минуты.'}
            </AnalyzingHint>
          </AnalyzingBar>
        </Section>
      )}

      <Section>
        <ContractHeader>
          <h4 style={{ margin: 0 }}>Связанный договор</h4>
          {contract && !editingContract && !pendingUnlink && !pendingLink && (
            <ContractActions>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => setEditingContract(true)}
                disabled={isLinking || isUnlinking || isProcessing || !can_write}
                title={!can_write ? 'Только чтение' : undefined}
              >
                Изменить
              </Button>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => setPendingUnlink(true)}
                disabled={isLinking || isUnlinking || isProcessing || !can_write}
                title={!can_write ? 'Только чтение' : undefined}
                style={{ color: 'var(--osnova-danger, #d04848)' }}
              >
                Отвязать
              </Button>
            </ContractActions>
          )}
          {editingContract && !pendingLink && (
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={() => setEditingContract(false)}
              disabled={isLinking}
            >
              Отмена
            </Button>
          )}
        </ContractHeader>

        {pendingLink ? (
          <ConfirmRow>
            <ConfirmText>
              <strong>Связать с этим договором?</strong>
              <br />
              <span style={{ opacity: 0.85 }}>{pendingLinkLabel ?? pendingLink}</span>
              <br />
              <small style={{ opacity: 0.7 }}>
                Запустится повторный анализ с этим договором — текущие риски будут заменены новыми. Это занимает 1-3
                минуты и расходует LLM-токены.
              </small>
            </ConfirmText>
            <ConfirmActions>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => {
                  setPendingLink(null);
                  setPendingLinkLabel(null);
                }}
                disabled={isLinking}
              >
                Отмена
              </Button>
              <Button
                type="button"
                $size="sm"
                onClick={handleLink}
                disabled={isLinking || !can_write}
                title={!can_write ? 'Только чтение' : undefined}
              >
                {isLinking ? 'Запускаем…' : 'Связать и переанализировать'}
              </Button>
            </ConfirmActions>
          </ConfirmRow>
        ) : pendingUnlink ? (
          <ConfirmRow>
            <ConfirmText>
              <strong>Отвязать договор?</strong>
              <br />
              <small style={{ opacity: 0.7 }}>
                Запустится повторный анализ <strong>без</strong> contract-блока — LLM будет анализировать претензию
                только на основе норм и судебной практики. Это занимает 1-3 минуты и расходует LLM-токены.
              </small>
            </ConfirmText>
            <ConfirmActions>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => setPendingUnlink(false)}
                disabled={isUnlinking}
              >
                Отмена
              </Button>
              <Button
                type="button"
                $size="sm"
                onClick={handleUnlink}
                disabled={isUnlinking || !can_write}
                title={!can_write ? 'Только чтение' : undefined}
                style={{
                  background: 'var(--osnova-danger, #d04848)',
                  borderColor: 'var(--osnova-danger, #d04848)',
                }}
              >
                {isUnlinking ? 'Отвязываем…' : 'Отвязать и переанализировать'}
              </Button>
            </ConfirmActions>
          </ConfirmRow>
        ) : contract && !editingContract ? (
          <>
            <LinkedContractCard
              type="button"
              onClick={() => {
                if (contract) {
                  window.open(`/statut/contracts?open=${contract.id}`, '_self');
                }
              }}
              aria-label={`Открыть карточку договора с ${contract.counterparty}`}
            >
              <LinkedContractRow>
                <strong>{contract.counterparty}</strong>
                <span style={{ color: 'inherit', opacity: 0.6 }}>·</span>
                <span style={{ flex: 1, minWidth: 0 }}>{contract.subject ?? '—'}</span>
                <Badge>{contractStatusLabel(contract.status)}</Badge>
              </LinkedContractRow>
            </LinkedContractCard>
            {relatedContractContext && relatedContractContext.total > 0 && (
              <ContextFeedback title="При анализе этой претензии LLM учла HITL-разметку связанного договора">
                <span style={{ opacity: 0.7 }}>LLM учла контекст договора:</span>{' '}
                {relatedContractContext.accepted > 0 && (
                  <>
                    <span style={{ color: 'var(--osnova-success, #4caf50)' }}>
                      ✓ {relatedContractContext.accepted} принятых
                    </span>
                    {(relatedContractContext.rejected > 0 ||
                      relatedContractContext.disputed > 0 ||
                      relatedContractContext.pending > 0) &&
                      ', '}
                  </>
                )}
                {relatedContractContext.rejected > 0 && (
                  <>
                    <span style={{ color: 'var(--osnova-danger, #d04848)' }}>
                      ✗ {relatedContractContext.rejected} отклонённых
                    </span>
                    {(relatedContractContext.disputed > 0 || relatedContractContext.pending > 0) && ', '}
                  </>
                )}
                {relatedContractContext.disputed > 0 && (
                  <>
                    <span style={{ color: 'var(--osnova-warning, #ff9800)' }}>
                      ? {relatedContractContext.disputed} спорных
                    </span>
                    {relatedContractContext.pending > 0 && ', '}
                  </>
                )}
                {relatedContractContext.pending > 0 && (
                  <span style={{ opacity: 0.7 }}>○ {relatedContractContext.pending} без разметки</span>
                )}
              </ContextFeedback>
            )}
          </>
        ) : editingContract ? (
          <ContractPicker
            counterpartyId={claim.counterparty_id ?? null}
            value={claim.contract_id}
            onSelect={(cid, label) => {
              setPendingLink(cid);
              setPendingLinkLabel(label ?? null);
            }}
            disabled={isLinking}
          />
        ) : (
          <>
            <MissingLinkNote>
              Договор не связан — это снижает качество анализа. Свяжите вручную:
            </MissingLinkNote>
            <ContractPicker
              counterpartyId={claim.counterparty_id ?? null}
              value={null}
              onSelect={(cid, label) => {
                setPendingLink(cid);
                setPendingLinkLabel(label ?? null);
              }}
              disabled={isLinking}
            />
          </>
        )}

        {linkNotice && <ReanalyzeNotice role="status">{linkNotice}</ReanalyzeNotice>}
      </Section>
    </>
  );
}
